import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { BTN_COLOR, THEME_COLOR3, THEME_COLOR4 } from '../../../../../constants';
import type { TMenuItemSlim } from '../types/menu-types';
import type { TModifier, TModifierGroup, TModifierView } from '../../modifiers/types';
import ModifierCard from './ModifierCard';
import ModifierPickerDialog from './ModifierPickerDialog';

type TModifiersFieldProps = {
  value: string[];
  onChange: (ids: string[]) => void;
  currencyCode: string;
  restaurantId: string;
  allModifiers: TModifier[];
  isLoadingModifiers?: boolean;
  menuItems: TMenuItemSlim[];
  categories: { _id: string; name: string }[];
};

const ModifiersField = ({
  value,
  onChange,
  currencyCode,
  restaurantId,
  allModifiers,
  isLoadingModifiers,
  menuItems,
  categories,
}: TModifiersFieldProps) => {
  const [panelOpen, setPanelOpen] = useState(true);
  const [pickerOpen, setPickerOpen] = useState(false);

  const modifiersById = useMemo(
    () => new Map(allModifiers.map((m) => [m._id, m])),
    [allModifiers]
  );

  const menuItemsById = useMemo(
    () => new Map(menuItems.map((item) => [item._id, item])),
    [menuItems]
  );

  const resolveView = (id: string): TModifierView | null => {
    const container = modifiersById.get(id);
    if (!container) return null;
    const group: TModifierGroup = container.modifiers?.length
      ? container.modifiers[0]
      : container;
    return {
      ...group,
      _idModifier: container._id,
      _idRestaurant: container._idRestaurant,
      __v: 0,
    };
  };

  const handleDelete = (id: string) => {
    onChange(value.filter((mid) => mid !== id));
  };

  return (
    <Box sx={s.panel}>
      {/* Panel header */}
      <Box sx={s.header}>
        <Box
          component="button"
          type="button"
          sx={s.headerToggle}
          onClick={() => setPanelOpen((p) => !p)}
        >
          <Typography sx={s.panelTitle}>Modifiers</Typography>
          {value.length > 0 && (
            <Box sx={s.badge}>{value.length}</Box>
          )}
        </Box>
        <Button
          type="button"
          size="small"
          variant="outlined"
          startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
          onClick={() => setPickerOpen(true)}
          sx={s.actionBtn}
        >
          Add Existing
        </Button>
        <Box
          component="button"
          type="button"
          onClick={() => setPanelOpen((p) => !p)}
          sx={s.expandBtn}
        >
          {panelOpen ? (
            <ExpandLessIcon sx={{ fontSize: 18 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 18 }} />
          )}
        </Box>
      </Box>

      <Collapse in={panelOpen}>
        <Box sx={s.body}>
          {value.length === 0 ? (
            <Box sx={s.empty}>
              <Typography sx={s.emptyText}>No modifiers linked</Typography>
              <Button
                type="button"
                size="small"
                variant="outlined"
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={() => setPickerOpen(true)}
                sx={s.emptyBtn}
              >
                Add existing modifier
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {value.map((id) => {
                const view = resolveView(id);
                if (!view) return null;
                return (
                  <ModifierCard
                    key={id}
                    modifier={view}
                    menuItemsById={menuItemsById}
                    currencyCode={currencyCode}
                    onDelete={handleDelete}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      </Collapse>

      <ModifierPickerDialog
        open={pickerOpen}
        currentIds={value}
        allModifiers={allModifiers}
        isLoadingModifiers={isLoadingModifiers}
        menuItems={menuItems}
        categories={categories}
        onSubmit={(ids) => {
          onChange(ids);
          setPickerOpen(false);
        }}
        onCancel={() => setPickerOpen(false)}
      />
    </Box>
  );
};

export default ModifiersField;

const s = {
  panel: {
    borderRadius: '12px',
    border: `1px solid ${THEME_COLOR4}`,
    backgroundColor: '#FFF',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    px: 2,
    py: 1.5,
  },
  headerToggle: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    p: 0,
    font: 'inherit',
    textAlign: 'left',
  },
  panelTitle: { fontFamily: 'GilroySemiBold', fontSize: 14, color: '#000' },
  badge: {
    backgroundColor: `${BTN_COLOR}1A`,
    color: BTN_COLOR,
    borderRadius: '999px',
    px: 1,
    py: 0.25,
    fontSize: 12,
    fontFamily: 'GilroySemiBold',
  },
  actionBtn: {
    textTransform: 'none' as const,
    fontFamily: 'GilroySemiBold',
    fontSize: 12,
    borderColor: THEME_COLOR4,
    color: '#000',
    borderRadius: '8px',
    height: 28,
    px: 1.5,
    '&:hover': { borderColor: '#000', backgroundColor: 'transparent' },
  },
  expandBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: THEME_COLOR3,
    display: 'flex',
    alignItems: 'center',
    p: 0,
  },
  body: {
    borderTop: `1px solid ${THEME_COLOR4}`,
    p: 1.5,
  },
  empty: {
    borderRadius: '10px',
    border: `1px dashed ${THEME_COLOR4}`,
    py: 3,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: 1.5,
  },
  emptyText: { fontFamily: 'GilroyRegular', fontSize: 12, color: THEME_COLOR3 },
  emptyBtn: {
    textTransform: 'none' as const,
    fontFamily: 'GilroySemiBold',
    fontSize: 12,
    borderColor: THEME_COLOR4,
    color: '#000',
    borderRadius: '8px',
    '&:hover': { borderColor: '#000', backgroundColor: 'transparent' },
  },
};
